/*
 * Loose Ends — gallery wall renderer. Owner: Angelo (§18).
 *
 * The wall is a full-screen OVERLAY, hidden by default. It opens when the join form finishes —
 * on `loose-ends:enter-club` or `loose-ends:welcome-closed` (both bubble to document) — so the
 * landing never shows the wall on scroll. It also opens directly via the `#gallery` URL hash, so
 * the gallery can be demoed on its own. Once open the gallery is permanent (a page refresh
 * returns to the landing); only the image detail overlay closes.
 *
 * Reads the JSON injected by sections/loose-ends-gallery.liquid ([data-le-gallery-data]) and
 * renders a bounded, pannable wall with momentum + a 1x↔2x zoom (spec §5.4). Tap a tile → detail.
 * The wall is built lazily on first open, so the canvas has real full-screen dimensions.
 *
 * NOTE: demo/starter renderer — mounts every tile as a DOM node (fine for the 150-tile cap).
 * The production canvas should recycle tiles — tracked as follow-up.
 */
(function () {
  'use strict';

  var TAP_THRESHOLD = 6;
  var MAX_ZOOM = 2;
  var MIN_ZOOM = 1;
  // The wall opens zoomed OUT, so the scale of the thing reads first.
  // Double-click / double-tap toggles up to MAX_ZOOM and back.
  var DEFAULT_ZOOM = MIN_ZOOM;

  function initGallery(section) {
    var canvas = section.querySelector('[data-le-canvas]');
    var dataEl = section.querySelector('[data-le-gallery-data]');
    var loading = section.querySelector('[data-le-loading]');
    if (!canvas || !dataEl) return;

    var tiles;
    try {
      tiles = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      tiles = [];
    }

    var built = false;
    var dragged = false;

    // --- feed state ----------------------------------------------------------
    // Which POST the detail view is showing, as an index into `tiles`. Declared out here, at
    // initGallery scope, deliberately: build()'s scope is where `x`, `y`, `scale`, `clamp()` and
    // `apply()` are already stranded, unreachable from the detail code that now needs to navigate.
    var feedIndex = -1;
    var feedOpen = false;

    /**
     * Wrap an index into `tiles`, so the feed loops instead of dead-ending (client's call — with
     * ~40 posts the repetition isn't visible, and a short feed that stops after three cards reads
     * as broken). Handles negatives, so swiping back from the first post lands on the last.
     */
    function wrap(i) {
      if (!tiles.length) return 0;
      return ((i % tiles.length) + tiles.length) % tiles.length;
    }

    // --- open / close --------------------------------------------------------
    // Which route got the visitor here, for the analytics event below. Set by each caller
    // rather than sniffed from an event object, because two of the four routes (the hash and a
    // member's auto-open) have no originating event to read.
    var openTrigger = 'auto';
    var openAnnounced = false;

    function open() {
      section.classList.add('is-open');
      document.body.classList.add('le-gallery-open');

      // Broadcast once (§10 gallery_open). It has to live here rather than in the analytics
      // module's own listeners: a signed-in member auto-opens the wall without ever firing
      // enter-club or welcome-closed, so listening for those alone would miss every returning
      // member. open() is the one place all four routes converge.
      if (!openAnnounced) {
        openAnnounced = true;
        document.dispatchEvent(new CustomEvent('loose-ends:gallery-open', {
          detail: { trigger: openTrigger, tile_count: tiles.length }
        }));
      }

      build(); // build after the overlay is visible → real dimensions
    }

    function openVia(trigger) {
      return function () {
        openTrigger = trigger;
        open();
      };
    }

    // Reveal triggers: the join form's finish events, or the #gallery hash for standalone demo.
    // The gallery is permanent once open — there is no close (a refresh returns to the landing).
    document.addEventListener('loose-ends:enter-club', openVia('enter_club'));
    document.addEventListener('loose-ends:welcome-closed', openVia('welcome_closed'));
    if (window.location.hash === '#gallery') openVia('hash')();

    // A visitor who just joined is a member but is NOT signed in, so Liquid rendered the empty
    // wall and the locked prompt. The service returns the wall with the /join response and the
    // form re-broadcasts it here — take those tiles and drop the lock, so they get their reward
    // straight away. The existing enter-club flow still does the opening, so the unlock
    // animation is untouched.
    //
    // On refresh they are back to logged-out with no join payload, so they see the locked
    // prompt and are asked to sign in — which is the intended trade.
    document.addEventListener('loose-ends:joined', function (event) {
      var payload = event.detail && event.detail.gallery;
      if (built || !payload || !payload.length) return;
      // Never swap the array out from under an open feed: `feedIndex` points into `tiles`, so
      // replacing it wholesale would leave the index addressing a different post. `built` already
      // makes this unreachable today, but the feed depends on it, so state it rather than imply it.
      if (feedOpen) return;
      tiles = payload;
      var locked = section.querySelector('[data-le-locked]');
      if (locked) locked.remove();
    });

    // Signed-in member: they skip the landing sections entirely, and this section is
    // display:none until .is-open — so open it on load or they land on a blank page.
    if (section.getAttribute('data-le-auto-open') === 'true') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', openVia('member_auto'));
      } else {
        openVia('member_auto')();
      }
    }

    // --- build the wall (once, on first open) --------------------------------
    function build() {
      if (built) return;
      built = true;

      if (!tiles.length) {
        if (loading) loading.textContent = 'No loose ends yet.';
        return;
      }

      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var gap = isMobile ? 16 : 32;
      var tileW = isMobile ? 150 : 210;
      var tileH = Math.round(tileW * (1920 / 1080));

      var world = document.createElement('div');
      world.className = 'le-gallery__world';

      var viewW = canvas.clientWidth || window.innerWidth;
      var viewH = canvas.clientHeight || window.innerHeight;
      var cols = Math.max(4, Math.ceil((viewW * 2.4) / (tileW + gap)));
      var rows = Math.max(4, Math.ceil((viewH * 2.4) / (tileH + gap)));

      // MOBILE: size the world from the POST COUNT, not the screen (client ask, 2026-09-07:
      // "150 on mobile"). The 2.4×-viewport world gives a phone only ~40 cells, so most of the
      // wall never got a tile. Every post gets a cell, in a world that keeps roughly the
      // viewport's aspect so panning feels the same in both directions. Desktop is untouched —
      // its 2.4× world already yields ~144 cells and the client's complaint was mobile-only.
      //
      // The cost of the bigger wall is paid by the loader below (images mount only near the
      // viewport), NOT here — cells themselves are two cheap DOM nodes each.
      if (isMobile) {
        var want = tiles.length;
        var ratio = (viewW / (tileW + gap)) / (viewH / (tileH + gap));
        cols = Math.max(4, Math.round(Math.sqrt(want * ratio)));
        rows = Math.max(4, Math.ceil(want / cols));
      }

      var worldW = cols * (tileW + gap) - gap;
      var worldH = rows * (tileH + gap) - gap;
      world.style.width = worldW + 'px';
      world.style.height = worldH + 'px';

      // Which template a post was rendered on. Posts seeded by hand carry no `theme`, so fall back
      // to a key unique to that post — an unknown template must never be treated as matching
      // another unknown one, or the ten seeds would all count as identical and get spread apart on
      // a guess. 📌 Backfilling `theme` on the seeded entries would make this exact.
      function groupOf(index) {
        var post = tiles[index];
        var t = post && post.theme;
        if (t === null || t === undefined || t === '') return 'u' + index;
        return 't' + t;
      }

      // Lay the wall out so no tile touches one of the same template.
      //
      // The naive `tiles[n % tiles.length]` walks the array in source order, which puts whatever
      // order Shopify returns straight onto the wall — and it returns the ten themeless seeds in
      // one contiguous block, so the wall opened on a visible slab of near-identical artwork.
      //
      // This picks, for each cell, the first post in a rotating queue whose template differs from
      // the tile to its LEFT and the tile ABOVE it (row-major, so those are the only two already
      // placed). The chosen post moves to the back of the queue, which keeps every post used about
      // equally rather than favouring the front of the array.
      //
      // Deliberately order-INDEPENDENT: it makes no assumption about how Liquid sorts metaobjects,
      // which is not documented and not ours to control.
      var queue = [];
      for (var q = 0; q < tiles.length; q++) queue.push(q);

      var placed = []; // post index per cell, in row-major order
      var cellCount = rows * cols;

      for (var cell = 0; cell < cellCount; cell++) {
        var col = cell % cols;
        var leftIdx = col === 0 ? -1 : placed[cell - 1];
        var aboveIdx = cell < cols ? -1 : placed[cell - cols];

        var pick = -1;
        for (var k = 0; k < queue.length; k++) {
          var cand = queue[k];
          // Reject a template clash, and also the same POST appearing beside itself — with more
          // tiles than posts every post repeats, and a visible duplicate is worse than a clash.
          if (leftIdx !== -1 && (cand === leftIdx || groupOf(cand) === groupOf(leftIdx))) continue;
          if (aboveIdx !== -1 && (cand === aboveIdx || groupOf(cand) === groupOf(aboveIdx))) continue;
          pick = k;
          break;
        }
        // Nothing satisfies both neighbours — possible with very few posts, or one template. Take
        // the next in rotation: a clash beats leaving a hole in the wall.
        if (pick === -1) pick = 0;

        var chosen = queue.splice(pick, 1)[0];
        queue.push(chosen);
        placed.push(chosen);
      }

      // MOBILE: viewport-driven image loading. Native `loading="lazy"` misjudges distance
      // inside a transform-PANNED world (nothing ever scrolls, so its heuristics see either
      // everything or nothing as "near"). IntersectionObserver measures the tile's real
      // on-screen position, transforms included, so with ~180 tiles only the ~30 near the
      // viewport hold an image at any moment; the rest load as they pan close. Encoded bytes
      // for the whole wall are single-digit MB; decoded memory for offscreen <img>s is the
      // engine's to reclaim, so images are never force-unloaded (removing src leaves stale
      // pixels in some engines anyway).
      //
      // Mobile also requests 360px thumbs instead of 540px — tiles render 150px wide, so 360
      // covers 2× screens at roughly half the decode cost. Snapped to one bucket so the CDN
      // cache stays shared.
      var lazyLoader = null;
      if (isMobile && 'IntersectionObserver' in window) {
        lazyLoader = new IntersectionObserver(
          function (entries) {
            for (var e = 0; e < entries.length; e++) {
              if (!entries[e].isIntersecting) continue;
              var lazyImg = entries[e].target.firstChild;
              if (lazyImg && !lazyImg.src) lazyImg.src = lazyImg.getAttribute('data-le-src');
              lazyLoader.unobserve(entries[e].target);
            }
          },
          { rootMargin: '700px' }
        );
      }
      function mobileThumb(url) {
        return url ? url.replace(/([?&]width=)\d+/, '$1360') : url;
      }

      var n = 0;
      for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) {
          // Name the index BEFORE incrementing n. The wall has more tiles than posts, so posts
          // repeat — a DOM tile is not a unique post, and the detail feed navigates posts, not
          // tiles. Capturing the index after the increment would open the NEXT post.
          var postIndex = placed[n];
          var data = tiles[postIndex];
          n++;
          var tile = document.createElement('button');
          tile.className = 'le-tile';
          tile.type = 'button';
          tile.style.left = c * (tileW + gap) + 'px';
          tile.style.top = r * (tileH + gap) + 'px';
          tile.style.width = tileW + 'px';
          tile.style.height = tileH + 'px';
          var img = document.createElement('img');
          img.alt = data.anon ? 'A loose end' : ('A loose end by ' + (data.name || 'a member'));
          if (lazyLoader) {
            // Deferred: src assigned when the tile pans near the viewport (observer above).
            img.setAttribute('data-le-src', mobileThumb(data.thumb || data.full));
            tile.appendChild(img);
            lazyLoader.observe(tile);
          } else {
            // Desktop, and any browser without IntersectionObserver: today's behaviour.
            img.loading = 'lazy';
            img.src = data.thumb || data.full;
            tile.appendChild(img);
          }
          (function (i) {
            tile.addEventListener('click', function () {
              if (dragged) return;
              openDetail(i);
            });
          })(postIndex);
          world.appendChild(tile);
        }
      }

      canvas.appendChild(world);
      if (loading) loading.remove();
      canvas.classList.add('is-ready');

      // Fetch the burst art now, while the visitor is still panning — see warmEmojiArt().
      warmEmojiArt();

      // --- pan + zoom --------------------------------------------------------
      var scale = DEFAULT_ZOOM;
      // Centre on the SCALED world, not the raw one — with scale > 1 the visual size is
      // worldW * scale, so using the unscaled width would open off-centre.
      var x = (viewW - worldW * scale) / 2;
      var y = (viewH - worldH * scale) / 2;

      function clamp() {
        var w = worldW * scale;
        var h = worldH * scale;
        var minX = Math.min(0, viewW - w);
        var minY = Math.min(0, viewH - h);
        x = Math.max(minX, Math.min(0, x));
        y = Math.max(minY, Math.min(0, y));
        if (w < viewW) x = (viewW - w) / 2;
        if (h < viewH) y = (viewH - h) / 2;
      }
      function apply() {
        world.style.transform = 'translate(' + x + 'px,' + y + 'px) scale(' + scale + ')';
      }
      clamp();
      apply();

      var down = false, lastX = 0, lastY = 0, vx = 0, vy = 0, moved = 0, raf = 0;

      function point(e) {
        var t = e.touches ? e.touches[0] : e;
        return { x: t.clientX, y: t.clientY };
      }
      function onDown(e) {
        down = true; dragged = false; moved = 0;
        cancelAnimationFrame(raf);
        var p = point(e); lastX = p.x; lastY = p.y; vx = vy = 0;
        world.style.willChange = 'transform'; // promote for smooth panning; dropped when settled
        canvas.classList.add('is-grabbing');
      }
      function onMove(e) {
        if (!down) return;
        var p = point(e);
        var dx = p.x - lastX, dy = p.y - lastY;
        lastX = p.x; lastY = p.y;
        moved += Math.abs(dx) + Math.abs(dy);
        if (moved > TAP_THRESHOLD) dragged = true;
        vx = dx; vy = dy; x += dx; y += dy;
        clamp(); apply();
        if (e.cancelable) e.preventDefault();
      }
      function onUp() {
        if (!down) return;
        down = false;
        canvas.classList.remove('is-grabbing');
        // Drop will-change once movement settles so the tiles re-rasterise crisply at the
        // current zoom (a permanent will-change would keep a blurry 1x bitmap).
        var settle = function () { world.style.willChange = 'auto'; };
        var step = function () {
          vx *= 0.94; vy *= 0.94; x += vx; y += vy;
          clamp(); apply();
          if (Math.abs(vx) > 0.4 || Math.abs(vy) > 0.4) { raf = requestAnimationFrame(step); }
          else { settle(); }
        };
        if (Math.abs(vx) > 0.4 || Math.abs(vy) > 0.4) { raf = requestAnimationFrame(step); }
        else { settle(); }
        setTimeout(function () { dragged = false; }, 0);
      }

      canvas.addEventListener('mousedown', onDown);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);

      // --- touch zoom ---------------------------------------------------------------------
      // The desktop bindings further down are `dblclick` and `wheel`. iOS Safari never
      // dispatches dblclick for touch input and there is no wheel event on touch, so without
      // the handlers below mobile has NO way to zoom at all — and `touch-action: none` on the
      // canvas also suppresses the browser's own pinch, so nothing picks up the slack.
      //
      // Pinch is the primary gesture. Double-tap cannot work on a tile: tiles open the detail
      // view on `click`, so the FIRST tap has already taken over the screen before a second
      // could arrive. It is therefore only wired up for taps that miss a tile.
      var pinch = null;
      var lastTapAt = 0;
      var lastTapX = 0;
      var lastTapY = 0;

      function pinchInfo(touches) {
        var a = touches[0];
        var b = touches[1];
        var dx = a.clientX - b.clientX;
        var dy = a.clientY - b.clientY;
        return {
          dist: Math.sqrt(dx * dx + dy * dy) || 1,
          cx: (a.clientX + b.clientX) / 2,
          cy: (a.clientY + b.clientY) / 2,
        };
      }

      function onTouchStart(e) {
        if (e.touches.length === 2) {
          pinch = { dist: pinchInfo(e.touches).dist, scale: scale };
          down = false; // suspend panning for the duration of the gesture
          dragged = true; // and stop the lift from registering as a tile tap
          cancelAnimationFrame(raf);
          canvas.classList.remove('is-grabbing');
          return;
        }
        onDown(e);
      }

      function onTouchMove(e) {
        if (pinch && e.touches.length === 2) {
          var info = pinchInfo(e.touches);
          // Zoom about the midpoint between the fingers, so the wall grows from where the
          // gesture is rather than from the centre of the screen. zoomAt clamps to 1x–2x.
          zoomAt(info.cx, info.cy, pinch.scale * (info.dist / pinch.dist));
          if (e.cancelable) e.preventDefault();
          return;
        }
        onMove(e);
      }

      function onTouchEnd(e) {
        if (pinch) {
          if (e.touches.length < 2) {
            pinch = null;
            world.style.willChange = 'auto';
            // Keep suppressing the tap that ends the gesture, then release the guard.
            setTimeout(function () { dragged = false; }, 0);
          }
          return;
        }

        var wasDrag = dragged;
        onUp(e);

        // Double-tap on empty canvas toggles 1x ↔ 2x, matching the desktop dblclick.
        var t = e.changedTouches && e.changedTouches[0];
        if (wasDrag || !t) return;
        if (e.target && e.target.closest && e.target.closest('.le-tile')) {
          lastTapAt = 0; // a tile tap opens the detail view; never treat it as tap one
          return;
        }
        var now = Date.now();
        if (
          now - lastTapAt < 320 &&
          Math.abs(t.clientX - lastTapX) < 32 &&
          Math.abs(t.clientY - lastTapY) < 32
        ) {
          zoomAt(t.clientX, t.clientY, scale > MIN_ZOOM ? MIN_ZOOM : MAX_ZOOM);
          lastTapAt = 0;
          return;
        }
        lastTapAt = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
      }

      canvas.addEventListener('touchstart', onTouchStart, { passive: true });
      canvas.addEventListener('touchmove', onTouchMove, { passive: false });
      canvas.addEventListener('touchend', onTouchEnd);
      canvas.addEventListener('touchcancel', onTouchEnd);

      function zoomAt(cx, cy, next) {
        next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
        if (next === scale) return;
        var rect = canvas.getBoundingClientRect();
        var px = cx - rect.left, py = cy - rect.top;
        x = px - ((px - x) * next) / scale;
        y = py - ((py - y) * next) / scale;
        scale = next;
        clamp(); apply();
      }
      canvas.addEventListener('dblclick', function (e) {
        zoomAt(e.clientX, e.clientY, scale > 1 ? 1 : MAX_ZOOM);
      });
      canvas.addEventListener('wheel', function (e) {
        e.preventDefault();
        zoomAt(e.clientX, e.clientY, scale + (e.deltaY < 0 ? 0.2 : -0.2));
      }, { passive: false });
    }

    // --- detail overlay ------------------------------------------------------
    var detail = section.querySelector('[data-le-detail]');
    var feedPrev = section.querySelector('[data-le-feed-prev]');
    var feedNext = section.querySelector('[data-le-feed-next]');
    var track = section.querySelector('[data-le-track]');
    var slideTpl = section.querySelector('[data-le-slide-template]');

    // --- the pager -----------------------------------------------------------
    // THREE slides, recycled. A decoded 1080×1920 bitmap is ~8MB, so three is ~25MB and five is
    // ~41MB — enough to get the tab killed on an older iPhone. Three is sufficient only because a
    // gesture is gated to one step at a time (see the settle lock in the gesture section).
    var SLOT_COUNT = 3;
    var slots = [];
    // `position` is a float: the integer part is the settled index, the fraction is drag/animation
    // progress. `feedIndex` is the committed integer. Position is DERIVED from the index and never
    // the other way round — deriving the index from a measured offset would feed the effect back
    // into its own input, which is the trap that produced the scroll-stack jitter.
    var position = 0;
    var slideH = 0;
    // One token PER SLOT. A single shared counter (what the old single-image lightbox used) is
    // wrong here: three loads are in flight at once, so bumping a global would cancel the other
    // two slides' images as a side effect of loading this one.
    var loadSeq = 0;
    var detailRequest = 0; // retained: closeDetail() uses it to invalidate everything at once

    // Shopify's CDN resizes on request, so asking for the width we will actually paint costs
    // nothing and roughly halves decoded memory on a phone. Snapped to buckets so every visitor
    // requests one of four URLs and the CDN cache stays shared — an arbitrary per-device width
    // would give every device its own cache entry.
    var WIDTH_BUCKETS = [540, 720, 900, 1080];

    function bucketWidth() {
      var css = (track && track.clientWidth) || window.innerWidth || 540;
      var want = css * (window.devicePixelRatio || 1);
      for (var i = 0; i < WIDTH_BUCKETS.length; i++) {
        if (WIDTH_BUCKETS[i] >= want) return WIDTH_BUCKETS[i];
      }
      return 1080;
    }

    function sizedUrl(url, px) {
      if (!url) return null;
      if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + px);
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + px;
    }

    function slotFor(index) {
      return ((index % SLOT_COUNT) + SLOT_COUNT) % SLOT_COUNT;
    }

    function buildSlots() {
      if (!track || !slideTpl || slots.length) return;
      for (var i = 0; i < SLOT_COUNT; i++) {
        var frag = slideTpl.content.cloneNode(true);
        var el = frag.querySelector('.le-detail__slide');
        var img = frag.querySelector('img');
        track.appendChild(frag);
        slots.push({ el: el, img: img, index: null, token: 0 });
      }
    }

    /**
     * Measured ONCE per open, from the track — a box this effect never moves. Re-measured only on
     * orientation change and on resizes big enough not to be the iOS URL bar collapsing; a slide
     * height that changed mid-gesture would drift the feed under the visitor's thumb.
     */
    function measure() {
      if (!track) return;
      slideH = track.clientHeight || window.innerHeight || 0;
    }

    /**
     * Write the REAL header height into `--le-detail-header-h`, which the desktop card's height
     * budget subtracts.
     *
     * `--le-header-h` claims 72px; the header actually renders around 90px (a known, recorded
     * discrepancy). Desktop keeps its header visible, so trusting the stale value made the card
     * overlap it. Measuring costs one read at open and retires the discrepancy for this view.
     */
    function measureChrome() {
      if (!detail) return;
      var header = document.querySelector('.section-header, .header-wrapper, .shopify-section-group-header-group');
      var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      if (h > 0 && h < 240) detail.style.setProperty('--le-detail-header-h', h + 'px');
    }

    /**
     * Keep `slideH` honest.
     *
     * The track's height is not guaranteed stable: it derives from the card's width, and anything
     * that changes the card's layout changes it. That bit once already — the card was
     * shrink-to-fitting to the action bar, whose width depends on how many digits the reaction
     * counts have, so the container resized per post and the neighbouring slide peeked in at the
     * bottom edge. The CSS fix removes that particular cause; this removes the whole class of it.
     *
     * Observing the TRACK is safe: the effect moves the slides inside it, never the track itself,
     * so this cannot feed back into its own input.
     */
    var trackObserver = null;
    function watchTrack() {
      if (trackObserver || !track || typeof window.ResizeObserver !== 'function') return;
      trackObserver = new window.ResizeObserver(function () {
        if (!feedOpen) return;
        var before = slideH;
        measure();
        if (slideH !== before) renderSlides();
      });
      trackObserver.observe(track);
    }

    function loadSlot(slot, index) {
      if (slot.index === index) return; // already showing this post
      slot.index = index;

      var post = tiles[wrap(index)];
      if (!post || !slot.img) return;

      var token = ++loadSeq;
      slot.token = token;

      slot.img.alt = post.anon ? 'A loose end' : 'A loose end by ' + (post.name || 'a member');
      if (slot.el) {
        slot.el.setAttribute('aria-label', 'Loose end ' + (wrap(index) + 1) + ' of ' + tiles.length);
      }

      // 1. Thumb first. The wall already fetched this exact URL, so it is in the HTTP cache and
      //    paints immediately — which is what stops a slide ever being blank while you swipe onto
      //    it. The spinner becomes a rare path rather than the normal one.
      var thumb = post.thumb || post.full;
      if (thumb) slot.img.src = thumb;

      // The blurred letterbox fill (mobile) reads this. Deliberately the thumb: it is the already
      // cached URL, and at a 28px blur nobody can tell the source was 540px wide. CSS.escape is
      // not available everywhere, so quote it and strip the few characters that could break out.
      if (slot.el) {
        if (thumb) {
          slot.el.style.setProperty('--le-slide-bg', 'url("' + String(thumb).replace(/["\\)]/g, '') + '")');
        } else {
          slot.el.style.removeProperty('--le-slide-bg');
        }
      }

      // 2. Upgrade to full res behind it.
      var full = sizedUrl(post.full || post.thumb, bucketWidth());
      if (!full || full === thumb) return;

      var loader = new Image();
      loader.onload = function () {
        if (slot.token !== token) return; // this slot was recycled onto another post
        slot.img.src = full;
      };
      loader.onerror = function () {
        /* keep the thumb — a soft image beats a blank slide */
      };
      loader.src = full;
    }

    /**
     * Point the three slots at {index-1, index, index+1}.
     *
     * INVARIANT: never repoint a slot that is within one viewport of the current position. Moving
     * one step swaps exactly one slot — the one holding index-1, which slotFor() maps to the same
     * slot as index+2 — and by the time it is repointed it is two slides away and off-screen. That
     * is what makes recycling flash-free by construction rather than by timing luck. Callers must
     * therefore only call this at rest, never mid-drag.
     */
    function fillSlots(index) {
      for (var d = -1; d <= 1; d++) {
        var target = index + d;
        loadSlot(slots[slotFor(target)], target);
      }
    }

    function renderSlides() {
      for (var i = 0; i < slots.length; i++) {
        var s = slots[i];
        if (s.index === null) continue;
        s.el.style.transform = 'translate3d(0,' + (s.index - position) * slideH + 'px,0)';
      }
    }

    /** Warm the post one beyond the direction of travel, so the next flick has it decoded. */
    var warming = false;
    function warmAhead(dir) {
      if (warming || !dir || tiles.length < 3) return;
      var post = tiles[wrap(feedIndex + dir * 2)];
      if (!post) return;
      var url = sizedUrl(post.full || post.thumb, bucketWidth());
      if (!url) return;
      warming = true;
      var img = new Image();
      var done = function () { warming = false; };
      img.onload = done;
      img.onerror = done;
      img.src = url;
    }

    // --- navigation ----------------------------------------------------------
    var settleRaf = 0;
    var settling = false;

    function cancelSettle() {
      if (settleRaf) window.cancelAnimationFrame(settleRaf);
      settleRaf = 0;
      settling = false;
    }

    /**
     * Driven by rAF rather than a CSS transition, deliberately: drag and settle then share one
     * code path writing one property, and there is no `transitionend` to miss when a gesture
     * interrupts an animation mid-flight.
     */
    function animateTo(target, dir) {
      var from = position;
      var dist = Math.abs(target - from);
      if (!dist || !slideH) {
        position = target;
        renderSlides();
        finishSettle(dir);
        return;
      }

      var dur = Math.max(180, Math.min(320, 320 * dist));
      var t0 = null;
      settling = true;

      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        position = from + (target - from) * eased;
        renderSlides();
        if (p < 1) {
          settleRaf = window.requestAnimationFrame(step);
          return;
        }
        settleRaf = 0;
        position = target;
        renderSlides();
        finishSettle(dir);
      }

      settleRaf = window.requestAnimationFrame(step);
    }

    function finishSettle(dir) {
      settling = false;
      if (detail) detail.classList.remove('is-transit');
      // Only now is recycling safe — everything more than one slide away is off-screen.
      fillSlots(feedIndex);
      renderSlides();
      warmAhead(dir);
      announcePost();
      scheduleFeedView();
    }

    /**
     * The single entry point for moving the feed. Chevrons, keyboard, wheel and swipe all end up
     * here, so the commit rules live in exactly one place.
     */
    function goTo(target, opts) {
      opts = opts || {};
      if (!feedOpen || tiles.length < 2) return;
      // One flick, one post: ignore anything arriving while a settle is still running. This is
      // also what keeps a 3-slide window sufficient — you can never be more than one step from
      // a loaded neighbour.
      if (settling && !opts.interrupt) return;
      cancelSettle();

      var dir = target === feedIndex ? 0 : target > feedIndex ? 1 : -1;
      feedIndex = target;

      // Commit the bar as the move STARTS, not when it ends, so the incoming post's counts are
      // already right as it lands. Rendering on arrival makes the numbers visibly re-tick after
      // the card has settled, which reads as a bug.
      renderReactions(currentPost());
      if (detail) detail.classList.add('is-transit');

      // A jump of more than one slide has no loaded neighbour to animate through, so snap.
      var jump = Math.abs(target - position) > 1.5;
      if (jump || opts.animate === false || reduceMotionMq.matches) {
        position = target;
        fillSlots(feedIndex);
        renderSlides();
        finishSettle(dir);
        return;
      }

      animateTo(target, dir);
    }

    function nextPost() { goTo(feedIndex + 1); }
    function prevPost() { goTo(feedIndex - 1); }

    // --- accessibility -------------------------------------------------------
    var feedLive = section.querySelector('[data-le-feed-live]');
    var lastFocused = null;

    /**
     * Announce the settled post. Only the current slide is exposed — without this a screen reader
     * walks all three mounted slides and reads three cards for one visible post.
     */
    function announcePost() {
      for (var i = 0; i < slots.length; i++) {
        var s = slots[i];
        if (!s.el || s.index === null) continue;
        var isCurrent = s.index === feedIndex;
        if (isCurrent) s.el.removeAttribute('aria-hidden');
        else s.el.setAttribute('aria-hidden', 'true');
      }

      if (!feedLive) return;
      var post = currentPost();
      if (!post) return;
      var who = post.anon ? 'anonymous' : 'by ' + (post.name || 'a member');
      feedLive.textContent =
        'Loose end ' + (wrap(feedIndex) + 1) + ' of ' + tiles.length + ', ' + who;
    }

    /**
     * Manual Tab cycle. `inert` would be cleaner but is Safari 15.5+, and this campaign's traffic
     * is heavily iOS in-app browsers where that cannot be assumed.
     */
    function focusables() {
      if (!detail) return [];
      return Array.prototype.filter.call(
        detail.querySelectorAll('button:not([hidden]):not([disabled])'),
        function (el) { return el.offsetParent !== null || el === document.activeElement; }
      );
    }

    // --- feed analytics ------------------------------------------------------
    // `loose_ends_card_detail_open` is deliberately NOT fired per swipe. It already has dashboards
    // attached, and firing it on every card change would multiply it ~50x and silently redefine
    // what it measures — the worst kind of analytics break, because nothing errors. Two new events
    // carry the feed instead.
    var FEED_DWELL_MS = 1000; // a card must actually be looked at, not flicked past
    var FEED_VIEW_CAP = 40; // stop firing past this; the session summary carries the tail
    var feedSeen = null; // post ids counted this session — dedupes, which matters when looping
    var feedViewTimer = 0;
    var feedViews = 0;
    var feedMaxDepth = 0;
    var feedStartedAt = 0;
    var feedEntryId = null;
    var feedEntryIndex = 0;

    function startFeedSession(post) {
      feedSeen = {};
      feedSessionEnded = false;
      feedViews = 0;
      feedMaxDepth = 0;
      feedStartedAt = Date.now();
      feedEntryId = post ? post.id : null;
      // Depth is measured from where this session STARTED, not from index 0. Opening post 200 and
      // swiping once is a depth of 1, not 201 — measuring from zero would make max_depth a
      // restatement of which tile happened to be tapped.
      feedEntryIndex = feedIndex;
    }

    /** Fire a post view only once it has been settled and on screen for a full second. */
    function scheduleFeedView() {
      window.clearTimeout(feedViewTimer);
      if (!feedOpen || !feedSeen) return;

      var depth = Math.abs(feedIndex - feedEntryIndex);
      if (depth > feedMaxDepth) feedMaxDepth = depth;

      feedViewTimer = window.setTimeout(function () {
        if (!feedOpen || !feedSeen) return;
        var post = currentPost();
        if (!post || !post.id) return;
        if (feedSeen[post.id]) return; // already counted — looping must not double count
        if (feedViews >= FEED_VIEW_CAP) return;

        feedSeen[post.id] = true;
        feedViews++;

        document.dispatchEvent(new CustomEvent('loose-ends:feed-post', {
          detail: {
            id: post.id,
            theme: post.theme,
            anon: !!post.anon,
            position: wrap(feedIndex) + 1
          }
        }));
      }, FEED_DWELL_MS);
    }

    /**
     * One row per feed session — this is the event that actually answers "did the feed increase
     * engagement?", which no per-card event can.
     */
    var feedSessionEnded = false;
    function endFeedSession(reason) {
      window.clearTimeout(feedViewTimer);
      if (!feedSeen || feedSessionEnded) return;
      feedSessionEnded = true;

      document.dispatchEvent(new CustomEvent('loose-ends:feed-session', {
        detail: {
          entry_id: feedEntryId,
          posts_viewed: feedViews,
          max_depth: feedMaxDepth,
          duration_ms: Date.now() - feedStartedAt,
          reason: reason || 'close'
        }
      }));
      feedSeen = null;
    }

    // A phone that never fires unload still fires pagehide, so a session closed by swiping the
    // browser away is still recorded.
    window.addEventListener('pagehide', function () {
      if (feedOpen) endFeedSession('pagehide');
    });

    function trapFocus(e) {
      var list = focusables();
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function openDetail(index) {
      if (!detail || !track || !tiles.length) return;

      buildSlots();

      feedIndex = index;
      position = index;
      feedOpen = true;

      var d = currentPost();
      if (!d) return;

      // The action bar acts on whichever tile is open, and reaction state is per image — so it has
      // to be rebuilt for this post, not cleared. It USED to just reset everything to false, which
      // was right when nothing was recorded; now that a visitor's own reaction persists, clearing
      // would make their reaction vanish every time they reopened the post — broken for exactly the
      // person who engaged most.
      renderReactions(d);

      detail.classList.add('is-open');
      section.classList.add('is-detail-open'); // blurs the wall behind the image
      // Drives the header hide in le-campaign-chrome.liquid. On a full-screen feed the X is the
      // exit affordance, so the site header would only be chrome sitting on top of the artwork.
      document.body.classList.add('le-feed-open');

      // Nothing to navigate to with a single post — don't offer controls that do nothing.
      var multi = tiles.length > 1;
      if (feedPrev) feedPrev.hidden = !multi;
      if (feedNext) feedNext.hidden = !multi;

      // The tap that opened this can be followed by a synthetic click landing on whatever is now
      // under the finger — which, full-screen, can be a chevron. Deaf for 350ms kills the class.
      detail.style.pointerEvents = 'none';
      window.setTimeout(function () {
        detail.style.pointerEvents = '';
      }, 350);

      // Chrome first: the card's height budget subtracts the header, so measuring the track before
      // that would sample a box that is about to change.
      measureChrome();
      measure();
      watchTrack();
      fillSlots(feedIndex);
      renderSlides();

      // The wall keeps ~48 tiles mounted behind the feed; without this a screen reader can walk
      // straight out of the dialog into them.
      if (canvas) canvas.setAttribute('aria-hidden', 'true');
      lastFocused = document.activeElement;
      if (detailClose) detailClose.focus();
      announcePost();

      // Broadcast for analytics (§10 card_detail_open). Fired here rather than after the image
      // decodes, because the metric is "opened a card", not "saw it finish loading". Carries no
      // personal data — the post's own id/theme, nothing about the viewer.
      document.dispatchEvent(new CustomEvent('loose-ends:detail-open', {
        detail: { id: d.id, theme: d.theme, anon: !!d.anon }
      }));

      startFeedSession(d);
      scheduleFeedView();
    }

    function closeDetail() {
      detailRequest++;
      // Invalidate every slot's in-flight load, so nothing paints into a reopened feed.
      for (var i = 0; i < slots.length; i++) slots[i].token = ++loadSeq;
      cancelSettle();
      endFeedSession('close');
      feedOpen = false;
      document.body.classList.remove('le-feed-open'); // restores the site header

      if (canvas) canvas.removeAttribute('aria-hidden');
      // Send focus back to the tile that opened this. Wall tiles are real <button>s that live for
      // the page's lifetime, so the restore target is always still there.
      if (lastFocused && lastFocused.focus) lastFocused.focus();
      lastFocused = null;
      if (detail) {
        detail.classList.remove('is-open');
        detail.classList.remove('is-loading');
        detail.classList.remove('is-transit');
      }
      section.classList.remove('is-detail-open');
    }
    // --- action bar ----------------------------------------------------------
    var detailBar = section.querySelector('[data-le-detail-bar]');

    /**
     * The post being shown, derived from `feedIndex` rather than held as its own reference.
     *
     * This used to be a `currentTile` object reference set alongside the index. Two sources of
     * truth for "which post is open" is exactly the drift the feed cannot afford — once swiping
     * moves the index, anything still reading a stale reference reacts to, or downloads, the wrong
     * post. The index is the single source of truth; the post is always derived from it.
     */
    function currentPost() {
      return tiles[wrap(feedIndex)] || null;
    }

    /**
     * Reactions are VISUAL ONLY — a press animation and a toggled state, nothing is recorded or
     * counted. This mirrors the Only Human interaction bar it was ported from, whose handler is
     * also purely decorative. Making them real needs storage, a write endpoint and abuse
     * controls; see the note in docs/loose-ends-backend-status.md.
     */
    var reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    /**
     * Reaction burst — a faithful port of Only Human's `create3DFloatingElement`.
     *
     * OH branches on GSAP, but GSAP is NOT loaded on their page either (`_gsap` is commented out
     * in theme.liquid), so the rAF path below is literally what runs there. Same spawn geometry,
     * same easing, same magnitudes, same durations — only the class names differ.
     *
     * Appended to <body>: `position: fixed` inside the lightbox would still be clipped by the
     * card, and body clears every stacking context in between.
     */
    function easeOutBack(t) {
      var c1 = 1.70158 * 2;
      var c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    }

    function floatOne(button, type, slotIndex, delaySeconds) {
      var rect = button.getBoundingClientRect();

      var el = document.createElement('div');
      el.className = 'le-detail__float';

      // 3D emoji art if available, else fall back to the button's own flat icon (OH's onerror).
      var emojiUrl = detailBar && detailBar.getAttribute('data-emoji-' + type);
      var node;
      if (emojiUrl) {
        node = new Image();
        node.src = emojiUrl;
        node.alt = '';
        node.onerror = function () {
          var icon = button.querySelector('img');
          if (!icon || !node.parentNode) return;
          var fallback = icon.cloneNode(true);
          fallback.removeAttribute('width');
          fallback.removeAttribute('height');
          fallback.removeAttribute('loading');
          node.parentNode.replaceChild(fallback, node);
        };
      } else {
        var icon = button.querySelector('img');
        if (!icon) return;
        node = icon.cloneNode(true);
        node.removeAttribute('width');
        node.removeAttribute('height');
        node.removeAttribute('loading');
      }
      el.appendChild(node);

      // --- spawn position: left / centre / right above the button, plus jitter ---------------
      var centreX = rect.left + rect.width / 2;
      var centreY = rect.top + rect.height / 2;
      var offsetXBase = Math.max(48, rect.width * 1.25);
      var offsetYBase = -Math.max(12, rect.height * 0.55);
      var spawnOffsetX = 0;
      if (slotIndex % 3 === 0) spawnOffsetX = -offsetXBase;
      if (slotIndex % 3 === 2) spawnOffsetX = offsetXBase;
      var jitterX = (Math.random() - 0.5) * Math.max(12, rect.width * 0.28);
      var jitterY = (Math.random() - 0.5) * Math.max(6, rect.height * 0.08);
      el.style.left = centreX + spawnOffsetX + jitterX + 'px';
      el.style.top = centreY + offsetYBase + jitterY + 'px';
      el.style.transform = 'translate(-50%, -50%)';
      document.body.appendChild(el);

      // --- trajectory ------------------------------------------------------------------------
      var vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      var vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      var directionPool = [
        { x: 0, y: -1 },
        { x: -0.9, y: -1 },
        { x: -1.2, y: -1 },
        { x: 0.9, y: -1 },
        { x: 1.2, y: -1 },
      ];
      var dir = directionPool[Math.floor(Math.random() * directionPool.length)];
      var xMagnitude = 0.1 * vw + Math.random() * (0.22 * vw);
      var yMagnitude = 0.5 * vh + Math.random() * (0.35 * vh);
      var xTarget = dir.x * xMagnitude + (Math.random() - 0.5) * (0.06 * vw);
      var yTarget = dir.y * yMagnitude + (Math.random() - 0.5) * (0.04 * vh);

      // 3D tumble. These come from the GSAP timeline, which is the path that ran while Only
      // Human was live — its no-GSAP fallback drops rotation entirely, so porting that would
      // have lost the tumble. Reproduced here per-frame so we don't pull in GSAP.
      var rotationX = (Math.random() - 0.5) * 360;
      var rotationY = (Math.random() - 0.5) * 360;
      var rotationZ = (Math.random() - 0.5) * 180;

      var popScale = 2.7 + Math.random() * 0.6; // 2.7 – 3.3
      var floatScale = 2.2 + Math.random() * 0.5; // 2.2 – 2.7
      var floatDuration = 3.2 + Math.random() * 1.2; // 3.2 – 4.4s

      var POP_MS = 240;
      var FLOAT_MS = Math.max(100, floatDuration * 1000);
      var startAt = performance.now() + (delaySeconds ? delaySeconds * 1000 : 0);

      // Rotation waypoints, matching the two GSAP tweens.
      var popRX = rotationX * 0.05;
      var popRY = rotationY * 0.05;
      var popRZ = rotationZ * 0.05;
      var endRX = rotationX * 0.1;
      var endRY = rotationY * 0.1;
      var endRZ = rotationZ * 0.15;

      // GSAP composes translate → rotate → scale; keep that order or the drift and spin combine
      // differently.
      function frame(px, py, rx, ry, rz, s) {
        return (
          'translate(-50%, -50%) translate(' + px + 'px, ' + py + 'px)' +
          ' rotateX(' + rx + 'deg) rotateY(' + ry + 'deg) rotateZ(' + rz + 'deg)' +
          ' scale(' + s + ')'
        );
      }

      function step(now) {
        if (!el.parentNode) return; // removed early
        if (now < startAt) {
          window.requestAnimationFrame(step);
          return;
        }
        var elapsed = now - startAt;

        // Phase 1 — pop out, ease back.out(2). (easeOutBack below uses overshoot 1.70158 * 2,
        // which is exactly GSAP's back.out(2).)
        if (elapsed <= POP_MS) {
          var t = Math.min(1, elapsed / POP_MS);
          var e = easeOutBack(t);
          el.style.opacity = '1';
          el.style.transform = frame(0, 0, popRX * e, popRY * e, popRZ * e, e * popScale);
          window.requestAnimationFrame(step);
          return;
        }

        // Phase 2 — continuous linear rise, scaling down slightly and fading out.
        var t2 = Math.min(1, (elapsed - POP_MS) / FLOAT_MS);
        el.style.opacity = String(1 - t2);
        el.style.transform = frame(
          xTarget * t2,
          yTarget * t2,
          popRX + (endRX - popRX) * t2,
          popRY + (endRY - popRY) * t2,
          popRZ + (endRZ - popRZ) * t2,
          popScale + (floatScale - popScale) * t2
        );

        if (t2 < 1) window.requestAnimationFrame(step);
        else if (el.parentNode) el.remove();
      }
      window.requestAnimationFrame(step);
    }

    /**
     * Warm the 3D emoji art before anyone can click a reaction.
     *
     * floatOne() mints `new Image()` at click time, so the FIRST reaction on a page spends the
     * animation's opening frames waiting on the network: the visitor sees only the tail — emojis
     * leaving — and every click after that looks correct because the art is cached. Fetching the
     * three PNGs once up front removes the difference between the first burst and the rest.
     *
     * Called from build(), i.e. the first time the wall opens — NOT at page load. The three files
     * are ~75KB combined and the campaign landing is already pulling hero video; this must not
     * compete with that. By the time someone has panned the wall and opened a tile, it is long
     * done.
     *
     * decode(), not merely load: a downloaded-but-undecoded PNG still hitches on first paint,
     * which is precisely the frame being protected. Rejections are swallowed on purpose —
     * floatOne already falls back to the button's flat icon via onerror, so a failed warm must
     * degrade to today's behaviour rather than throw.
     *
     * Everything is looked up fresh rather than read from `detailBar` / `reduceMotionMq` above:
     * build() runs synchronously during init on the auto-open and #gallery paths, before those
     * are assigned, so depending on them would silently skip the warm for signed-in members —
     * the very people who land straight on the wall.
     */
    var emojiArtWarmed = false;
    function warmEmojiArt() {
      if (emojiArtWarmed) return;
      emojiArtWarmed = true;

      // Reduced motion never bursts, so there is nothing to warm — don't spend their bytes.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var bar = section.querySelector('[data-le-detail-bar]');
      if (!bar) return;

      ['like', 'heart', 'fire'].forEach(function (type) {
        var url = bar.getAttribute('data-emoji-' + type);
        if (!url) return;
        var img = new Image();
        img.src = url;
        if (typeof img.decode === 'function') {
          img.decode().catch(function () {});
        }
      });
    }

    function burst(button) {
      if (reduceMotionMq.matches) return;
      var type = button.getAttribute('data-le-react');
      // Exactly three (OH's burstCount), staggered TWICE the way OH does it: a 20ms setTimeout
      // per element plus a 0.05s delay inside the tween itself.
      for (var i = 0; i < 3; i++) {
        (function (index) {
          window.setTimeout(function () {
            floatOne(button, type, index, index * 0.05);
          }, index * 20);
        })(i);
      }
    }

    /* --- reaction counts ------------------------------------------------------------------
     * These numbers are CONTENT, not measurement. Nothing a visitor does is recorded anywhere,
     * and no two visitors share a count. NOYZ can set an exact number per post on the metaobject;
     * otherwise the number is derived from the post's own id.
     *
     * THE DERIVED NUMBER MUST BE STABLE. The obvious implementation — Math.random() when the
     * lightbox opens — gives a post 530 reactions on one view and 812 on the next, which anyone
     * reviewing the campaign spots immediately. Hashing the id instead produces something equally
     * arbitrary that never moves: same post, same count, every load, every device, no storage and
     * no backend write. The reaction type is hashed in too, or all three icons would show the
     * identical number and read as obviously fake.
     */
    var REACT_STORE_PREFIX = 'le-react-';

    /** FNV-1a. Small, fast, no dependencies, and well spread for short strings like these. */
    function hashString(str) {
      var h = 0x811c9dc5;
      for (var i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        // The classic 16777619 multiply, via shifts, to stay inside 32-bit int math.
        h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
      }
      return h >>> 0;
    }

    function derivedCount(postId, type) {
      var bar = section.querySelector('[data-le-detail-bar]');
      var min = parseInt((bar && bar.getAttribute('data-le-count-min')) || '100', 10);
      var max = parseInt((bar && bar.getAttribute('data-le-count-max')) || '1300', 10);
      if (!(max > min)) return min; // a misconfigured range should not produce NaN on screen
      return min + (hashString(String(postId) + ':' + type) % (max - min + 1));
    }

    function reactionKey(postId, type) {
      return REACT_STORE_PREFIX + String(postId) + '-' + type;
    }

    function hasReacted(postId, type) {
      try {
        return window.localStorage.getItem(reactionKey(postId, type)) === '1';
      } catch (e) {
        return false; // private mode / storage disabled — reactions just don't persist
      }
    }

    function setReacted(postId, type, on) {
      try {
        if (on) window.localStorage.setItem(reactionKey(postId, type), '1');
        else window.localStorage.removeItem(reactionKey(postId, type));
      } catch (e) {
        /* storage unavailable; the in-page state is still correct for this session */
      }
    }

    /** Base count for a post: the metaobject value if NOYZ set one, else the derived number. */
    function baseCount(tile, type) {
      var raw = tile && tile.reactions ? tile.reactions[type] : null;
      var n = typeof raw === 'number' ? raw : parseInt(raw, 10);
      return Number.isFinite(n) ? n : derivedCount(tile && tile.id, type);
    }

    /** Rebuild the whole bar for the post being opened, restoring this visitor's own reactions. */
    function renderReactions(tile) {
      var bar = section.querySelector('[data-le-detail-bar]');
      if (!bar || !tile) return;

      bar.querySelectorAll('[data-le-react]').forEach(function (button) {
        var type = button.getAttribute('data-le-react');
        var reacted = hasReacted(tile.id, type);
        var count = baseCount(tile, type) + (reacted ? 1 : 0);

        button.setAttribute('aria-pressed', reacted ? 'true' : 'false');
        var el = button.querySelector('[data-le-count]');
        if (el) el.textContent = String(count);
        // The count is aria-hidden (decorative), so put it in the label or a screen reader hears
        // only "Like this loose end" and never the number sighted users are reacting to.
        var base = button.getAttribute('data-le-label') || button.getAttribute('aria-label') || '';
        if (!button.getAttribute('data-le-label')) button.setAttribute('data-le-label', base);
        button.setAttribute('aria-label', button.getAttribute('data-le-label') + ' — ' + count + ' reactions');
      });
    }

    function react(button) {
      // Capture the post ONCE, on the first line, and use it for the rest of this call. The feed
      // can move while a press is being handled, so re-reading the current post after the toggle
      // would record the reaction against whichever post had arrived by then.
      var post = currentPost();

      button.classList.add('is-pressed');
      window.setTimeout(function () {
        button.classList.remove('is-pressed');
      }, 120);

      // Toggle: +1 on, −1 off. Not increment-per-click, so nobody can hammer a count upward and
      // make the number visibly meaningless.
      var on = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', on ? 'true' : 'false');

      if (post) {
        var type = button.getAttribute('data-le-react');
        setReacted(post.id, type, on);
        renderReactions(post);
      }

      // Only burst on the way IN. Un-reacting shouldn't celebrate.
      if (on) burst(button);
    }

    /**
     * Force a save rather than a navigation. A plain <a download> is ignored for cross-origin
     * URLs, and the CDN is a different origin — so fetch the bytes and hand the browser a blob.
     * Shopify's CDN sends `access-control-allow-origin: *`, so this is allowed.
     */
    function download(button) {
      // Captured once, for the same reason as react(): the fetch is async and the feed can move
      // while it is in flight, which would otherwise name the file after a different post.
      var post = currentPost();
      if (!post) return;
      var url = post.full || post.thumb;
      if (!url) return;

      button.classList.add('is-busy');
      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.blob();
        })
        .then(function (blob) {
          var objectUrl = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = objectUrl;
          a.download = 'loose-end-' + String(post.id || '').split('/').pop() + '.png';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
        })
        .catch(function () {
          // Last resort: open it so the visitor can save it manually.
          window.open(url, '_blank', 'noopener');
        })
        .then(function () {
          button.classList.remove('is-busy');
        });
    }

    if (detailBar) {
      detailBar.addEventListener('click', function (e) {
        var button = e.target.closest ? e.target.closest('[data-le-react], [data-le-download]') : null;
        if (!button) return;
        e.stopPropagation(); // the overlay closes on outside clicks — the bar is not "outside"
        if (button.hasAttribute('data-le-download')) download(button);
        else react(button);
      });
    }

    // The X is the only pointer route out.
    //
    // It USED to have no handler of its own — it closed by falling through a rule that treated any
    // click not on the image and not in the bar as "outside". That rule is gone: once the card
    // fills the viewport there is no outside left, so it would have closed the view on every tap on
    // the artwork, including the taps that begin a swipe.
    var detailClose = section.querySelector('[data-le-detail-close]');
    if (detailClose) {
      detailClose.addEventListener('click', function (e) {
        e.stopPropagation();
        closeDetail();
      });
    }

    if (feedPrev) feedPrev.addEventListener('click', function (e) { e.stopPropagation(); prevPost(); });
    if (feedNext) feedNext.addEventListener('click', function (e) { e.stopPropagation(); nextPost(); });

    /**
     * Click the surrounding space to close — the expected behaviour for anything that reads as a
     * modal over a page.
     *
     * The old version of this rule was "any click that is not the image and not the bar", which is
     * why it had to be deleted: full-screen, that matches the artwork itself and every tap that
     * begins a swipe. This asks a different question — is the click OUTSIDE THE CARD? — which is
     * self-correcting across breakpoints rather than duplicating one. On desktop the card is a
     * centred box, so the space around it closes. On mobile the card IS the viewport, so nothing
     * is ever outside it and this can never fire, including on the letterbox bands, which belong
     * to the card. No breakpoint is named here on purpose: the behaviour follows the layout.
     */
    if (detail) {
      detail.addEventListener('click', function (e) {
        var card = detail.querySelector('.le-detail__card');
        if (!card || card.contains(e.target)) return;
        closeDetail();
      });
    }

    // --- swipe ---------------------------------------------------------------
    // Pointer Events, not touch/mouse pairs: one path for finger, trackpad drag and mouse.
    //
    // The reason this is a transform pager at all is the `touch-action: none` on the track. The
    // browser never starts a scroll, so iOS momentum — which cannot be cancelled from JS and has
    // cost this project real time before — never comes into existence here.
    var COMMIT_RATIO = 0.18; // of a slide height
    var COMMIT_VELOCITY = 0.5; // px/ms
    var VELOCITY_WINDOW = 80; // ms

    var pointerId = null;
    var dragStartY = 0;
    var dragStartPos = 0;
    var samples = [];

    function pushSample(y) {
      var now = (window.performance && window.performance.now) ? window.performance.now() : Date.now();
      samples.push({ y: y, t: now });
      while (samples.length > 1 && now - samples[0].t > VELOCITY_WINDOW) samples.shift();
    }

    /**
     * Velocity over a short rolling WINDOW, not the last frame's delta.
     *
     * The wall's pan uses last-delta, which is fine as an input to momentum decay but wrong as a
     * threshold: a single stalled frame at lift-off reads as zero velocity, and the flick the
     * visitor definitely made is silently ignored.
     */
    function velocity() {
      if (samples.length < 2) return 0;
      var first = samples[0];
      var last = samples[samples.length - 1];
      var dt = last.t - first.t;
      if (dt <= 0) return 0;
      return (last.y - first.y) / dt;
    }

    if (track) {
      track.addEventListener('pointerdown', function (e) {
        if (!feedOpen || tiles.length < 2 || pointerId !== null) return;
        if (settling) return; // one flick, one post
        pointerId = e.pointerId;
        dragStartY = e.clientY;
        dragStartPos = position;
        samples = [];
        pushSample(e.clientY);
        if (detail) detail.classList.add('is-transit');
        if (track.setPointerCapture) {
          try { track.setPointerCapture(e.pointerId); } catch (err) { /* not capturable */ }
        }
      });

      track.addEventListener('pointermove', function (e) {
        if (pointerId !== e.pointerId || !slideH) return;
        pushSample(e.clientY);
        // Dragging DOWN (positive dy) reveals the previous post, so position decreases.
        position = dragStartPos - (e.clientY - dragStartY) / slideH;
        renderSlides();
      });

      function endDrag(e) {
        if (pointerId !== e.pointerId) return;
        pointerId = null;
        if (track.releasePointerCapture) {
          try { track.releasePointerCapture(e.pointerId); } catch (err) { /* already gone */ }
        }
        if (!slideH) return;

        var dy = e.clientY - dragStartY;
        var v = velocity();
        var far = Math.abs(dy) > slideH * COMMIT_RATIO;
        var fast = Math.abs(v) > COMMIT_VELOCITY;

        var dir = 0;
        if (far || fast) dir = (fast ? -v : -dy) > 0 ? 1 : -1;

        // `interrupt` because the drag itself set is-transit and we are the one finishing it.
        goTo(feedIndex + dir, { interrupt: true });
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
    }

    // --- desktop wheel -------------------------------------------------------
    // Deliberately NOT integrating wheel deltas into `position`. One trackpad flick emits dozens
    // of events over ~1s, so integrating them reimplements the exact momentum problem this design
    // exists to avoid — the visitor would blow through five posts in one gesture. Instead: the
    // first event past a threshold commits one step, then wheels are ignored until they go quiet.
    var WHEEL_QUIET_MS = 150;
    var wheelLocked = false;
    var wheelTimer = 0;

    if (track) {
      track.addEventListener('wheel', function (e) {
        if (!feedOpen || tiles.length < 2) return;
        e.preventDefault();

        window.clearTimeout(wheelTimer);
        wheelTimer = window.setTimeout(function () { wheelLocked = false; }, WHEEL_QUIET_MS);

        if (wheelLocked || Math.abs(e.deltaY) < 4) return;
        wheelLocked = true;
        goTo(feedIndex + (e.deltaY > 0 ? 1 : -1));
      }, { passive: false });
    }

    // --- keyboard ------------------------------------------------------------
    // Extends the one existing document handler rather than adding a second, so the theme editor
    // re-rendering the section cannot accumulate duplicates that each move the feed a step.
    document.addEventListener('keydown', function (e) {
      if (!feedOpen) return;

      switch (e.key) {
        case 'Escape':
          closeDetail();
          break;
        case 'ArrowDown':
        case 'PageDown':
          e.preventDefault();
          nextPost();
          break;
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          prevPost();
          break;
        case 'Home':
          e.preventDefault();
          goTo(0);
          break;
        case 'End':
          e.preventDefault();
          goTo(tiles.length - 1);
          break;
        case 'Tab':
          trapFocus(e);
          break;
        default:
          break;
      }
    });

    // --- resize --------------------------------------------------------------
    // Only re-measure on a real layout change. A phone's URL bar collapsing fires resize with a
    // delta of ~60-90px; re-measuring on that would resize every slide mid-swipe.
    var lastH = window.innerHeight;
    function onResize() {
      if (!feedOpen) { lastH = window.innerHeight; return; }
      if (Math.abs(window.innerHeight - lastH) < 120) return;
      lastH = window.innerHeight;
      measure();
      renderSlides();
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', function () {
      lastH = window.innerHeight;
      window.setTimeout(function () {
        measure();
        renderSlides();
      }, 200); // after the browser has settled the new viewport
    });
  }

  function init() {
    document.querySelectorAll('[data-le-section="gallery"]').forEach(initGallery);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
